<script setup lang="ts">
import type { TocLink } from '@nuxt/content'
import type { HTMLAttributes } from 'vue'

const props = defineProps<{
  links?: TocLink[]
  listClass?: HTMLAttributes['class']
}>()

const sanitizeValue = (value: string) =>
  value.replace(/^[a-zA-Z0-9]+[.)]?\s*/, '')

const activeToc = ref<string | undefined>()
const observer = ref<IntersectionObserver>()

// Flatten all links including children for easier processing
const flatLinks = computed(() => {
  const flat: { id: string; element?: Element }[] = []

  props.links?.forEach((link) => {
    flat.push({ id: link.id })
    if (link.children) {
      link.children.forEach((child) => {
        flat.push({ id: child.id })
      })
    }
  })

  return flat
})

onMounted(() => {
  // Create intersection observer
  observer.value = new IntersectionObserver(
    (entries) => {
      // Find the entry that's most visible
      const visibleEntries = entries.filter((entry) => entry.isIntersecting)

      if (visibleEntries.length > 0) {
        // Sort by intersection ratio (most visible first)
        visibleEntries.sort((a, b) => b.intersectionRatio - a.intersectionRatio)

        // Get the ID from the most visible element
        const mostVisible = visibleEntries[0]
        const id = mostVisible?.target.id

        if (id && flatLinks.value.some((link) => link.id === id)) {
          activeToc.value = id
        }
      }
    },
    {
      rootMargin: '-100px 0px -60% 0px', // Trigger when element is near the top
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
    }
  )

  // Observe all heading elements
  nextTick(() => {
    flatLinks.value.forEach((link) => {
      const element = document.getElementById(link.id)
      if (element && observer.value) {
        observer.value.observe(element)
        link.element = element
      }
    })
  })
})

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})

// Handle manual clicks to update active state immediately
const handleTocClick = (id: string) => {
  activeToc.value = id
}
</script>
<template>
  <div class="lg:sticky lg:top-navbar-safe">
    <p class="font-bold mb-3">Akses Cepat</p>
    <ol
      :class="
        cn(
          'space-y-3 list-decimal pl-4 text-sm marker:text-on-surface-variant',
          props.listClass
        )
      "
    >
      <li v-for="item in links" :key="item.id">
        <a
          :href="`#${item.id}`"
          :class="
            cn(
              'hover:underline font-medium block transition-colors duration-200',
              {
                'text-primary': activeToc === item.id
              }
            )
          "
          @click="handleTocClick(item.id)"
        >
          {{ sanitizeValue(item.text) }}
        </a>
        <ul v-if="item.children" class="pl-4 list-decimal mt-3 space-y-3">
          <li v-for="child in item.children" :key="child.id">
            <a
              :href="`#${child.id}`"
              :class="
                cn(
                  'hover:underline font-medium block transition-colors duration-200',
                  {
                    'text-primary': activeToc === child.id
                  }
                )
              "
              @click="handleTocClick(child.id)"
            >
              {{ sanitizeValue(child.text) }}
            </a>
          </li>
        </ul>
      </li>
    </ol>
  </div>
</template>
